import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Upload, FileText, CheckCircle, Clock, AlertCircle, 
  CreditCard, User, Calendar, Shield, ArrowRight, X,
  TrendingUp, FileCheck, Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../lib/auth';
import { clientAPI, paymentAPI } from '../lib/api';
import { toast } from 'sonner';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [creditItems, setCreditItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  const loadDashboardData = useCallback(async () => {
    try {
      const [profileRes, docsRes, updatesRes, itemsRes] = await Promise.all([
        clientAPI.getProfile(),
        clientAPI.getDocuments(),
        clientAPI.getUpdates(),
        clientAPI.getCreditItems(),
      ]);
      setProfile(profileRes.data);
      setDocuments(docsRes.data);
      setUpdates(updatesRes.data);
      setCreditItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePaymentCallback = useCallback(async () => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');

    if (sessionId && paymentStatus === 'success') {
      setProcessingPayment(true);
      try {
        let attempts = 0;
        const maxAttempts = 5;
        const poll = async () => {
          const res = await paymentAPI.getPaymentStatus(sessionId);
          if (res.data.payment_status === 'paid') {
            toast.success('Payment successful! Your subscription is now active.');
            loadDashboardData();
            navigate('/dashboard', { replace: true });
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 2000);
          } else {
            toast.info('Payment is being processed. Please refresh in a few minutes.');
            navigate('/dashboard', { replace: true });
          }
        };
        await poll();
      } catch (error) {
        toast.error('Failed to verify payment status');
      } finally {
        setProcessingPayment(false);
      }
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled');
      navigate('/dashboard', { replace: true });
    }
  }, [searchParams, navigate, loadDashboardData]);

  useEffect(() => {
    loadDashboardData();
    handlePaymentCallback();
  }, [loadDashboardData, handlePaymentCallback]);

  const handlePayment = async (packageType) => {
    try {
      const res = await paymentAPI.createCheckout(packageType);
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error('Failed to initiate payment');
    }
  };

  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDoc(docType);
    try {
      await clientAPI.uploadDocument(docType, file);
      toast.success('Document uploaded successfully');
      const docsRes = await clientAPI.getDocuments();
      setDocuments(docsRes.data);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'status-badge status-pending',
      active: 'status-badge status-active',
      completed: 'status-badge status-completed',
      cancelled: 'status-badge status-cancelled',
      disputed: 'status-badge status-pending',
      removed: 'status-badge status-completed',
      verified: 'status-badge status-active',
    };
    return <span className={styles[status] || 'status-badge'}>{status}</span>;
  };

  const getCompletionProgress = () => {
    let progress = 0;
    if (profile?.subscription_status === 'active') progress += 25;
    if (documents.some(d => d.document_type === 'government_id')) progress += 25;
    if (documents.some(d => d.document_type === 'proof_of_residence')) progress += 25;
    if (profile?.credit_monitoring_login) progress += 25;
    return progress;
  };

  const renderDocumentSlot = (doc, uploaded) => {
    if (uploaded) {
      return (
        <>
          <CheckCircle className="h-10 w-10 text-teal-600 mx-auto mb-3" />
          <p className="font-semibold text-teal-700">{doc.label}</p>
          <p className="text-xs text-teal-600 mt-1">Uploaded</p>
        </>
      );
    }
    if (uploadingDoc === doc.type) {
      return (
        <>
          <div className="spinner mx-auto mb-3"></div>
          <p className="text-slate-600">Uploading...</p>
        </>
      );
    }
    return (
      <>
        <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
        <p className="font-semibold text-slate-700">{doc.label}</p>
        <p className="text-xs text-slate-500 mt-1">{doc.description}</p>
      </>
    );
  };

  const documentTypes = [
    { type: 'government_id', label: 'Government ID', description: "Driver's License or Passport" },
    { type: 'proof_of_residence', label: 'Proof of Residence', description: 'Utility Bill or Bank Statement' },
    { type: 'ssn_card', label: 'Social Security Card', description: 'If available' },
  ];

  if (loading) {
    return (
      <Layout showFooter={false}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showFooter={false}>
      <div className="min-h-screen bg-slate-50 py-8" data-testid="client-dashboard">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-slate-900">
              Welcome, {user?.full_name}
            </h1>
            <p className="text-slate-600 mt-1">
              Track your Credit Restoration progress and manage your account
            </p>
          </div>

          {/* Progress Card */}
          <Card className="mb-8 border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-slate-900">Account Setup Progress</h2>
                <span className="text-2xl font-bold text-amber-700">{getCompletionProgress()}%</span>
              </div>
              <Progress value={getCompletionProgress()} className="h-2 mb-4" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  {profile?.subscription_status === 'active' ? (
                    <CheckCircle className="h-4 w-4 text-teal-600" />
                  ) : (
                    <Clock className="h-4 w-4 text-slate-400" />
                  )}
                  <span className={profile?.subscription_status === 'active' ? 'text-teal-700' : 'text-slate-500'}>
                    Payment
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {documents.some(d => d.document_type === 'government_id') ? (
                    <CheckCircle className="h-4 w-4 text-teal-600" />
                  ) : (
                    <Clock className="h-4 w-4 text-slate-400" />
                  )}
                  <span className={documents.some(d => d.document_type === 'government_id') ? 'text-teal-700' : 'text-slate-500'}>
                    ID Upload
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {documents.some(d => d.document_type === 'proof_of_residence') ? (
                    <CheckCircle className="h-4 w-4 text-teal-600" />
                  ) : (
                    <Clock className="h-4 w-4 text-slate-400" />
                  )}
                  <span className={documents.some(d => d.document_type === 'proof_of_residence') ? 'text-teal-700' : 'text-slate-500'}>
                    Address Proof
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {profile?.credit_monitoring_login ? (
                    <CheckCircle className="h-4 w-4 text-teal-600" />
                  ) : (
                    <Clock className="h-4 w-4 text-slate-400" />
                  )}
                  <span className={profile?.credit_monitoring_login ? 'text-teal-700' : 'text-slate-500'}>
                    Credit Monitoring
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Required Alert */}
          {profile?.subscription_status !== 'active' && (
            <Card className="mb-8 border-amber-300 bg-amber-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900 mb-1">Payment Required</h3>
                    <p className="text-amber-800 text-sm mb-4">
                      To begin your Credit Restoration journey, please complete the payment process.
                      $29/month for credit monitoring is required.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button 
                        onClick={() => handlePayment('monthly')}
                        className="bg-amber-600 hover:bg-amber-700"
                        disabled={processingPayment}
                        data-testid="pay-monthly-btn"
                      >
                        <CreditCard className="mr-2 h-4 w-4" />
                        Pay $29 Monthly
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Tabs */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
              <TabsTrigger value="updates" data-testid="tab-updates">Updates</TabsTrigger>
              <TabsTrigger value="credit-items" data-testid="tab-credit-items">Credit Items</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid md:grid-cols-3 gap-6">
                {/* Status Card */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Shield className="h-5 w-5 text-amber-700" />
                      Account Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Status</span>
                        {getStatusBadge(profile?.status || 'pending')}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Subscription</span>
                        {getStatusBadge(profile?.subscription_status || 'inactive')}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Documents Card */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileCheck className="h-5 w-5 text-amber-700" />
                      Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Uploaded</span>
                        <span className="font-semibold">{documents.length} / 3</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Pending</span>
                        <span className="font-semibold">{3 - documents.length}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Credit Items Card */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <TrendingUp className="h-5 w-5 text-amber-700" />
                      Credit Items
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">In Progress</span>
                        <span className="font-semibold">
                          {creditItems.filter(i => i.status === 'disputed').length}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Removed</span>
                        <span className="font-semibold text-teal-600">
                          {creditItems.filter(i => i.status === 'removed').length}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Updates */}
              {updates.length > 0 && (
                <Card className="mt-6 border-slate-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="h-5 w-5 text-amber-700" />
                      Recent Updates
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {updates.slice(0, 3).map((update) => (
                        <div key={update.id} className="flex gap-4 p-4 bg-slate-50 rounded-lg">
                          <div className="timeline-dot flex-shrink-0"></div>
                          <div>
                            <h4 className="font-semibold text-slate-900">{update.title}</h4>
                            <p className="text-sm text-slate-600">{update.description}</p>
                            <p className="text-xs text-slate-400 mt-2">
                              {new Date(update.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Upload Documents</CardTitle>
                  <CardDescription>
                    Upload the required documents to verify your identity and begin the Credit Restoration process.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    {documentTypes.map((doc) => {
                      const uploaded = documents.find(d => d.document_type === doc.type);
                      return (
                        <div 
                          key={doc.type} 
                          className={`dropzone ${uploaded ? 'border-teal-300 bg-teal-50' : ''}`}
                        >
                          <input
                            type="file"
                            id={doc.type}
                            className="hidden"
                            onChange={(e) => handleDocumentUpload(e, doc.type)}
                            accept=".pdf,.jpg,.jpeg,.png"
                            disabled={uploadingDoc === doc.type}
                          />
                          <label htmlFor={doc.type} className="cursor-pointer block">
                            {renderDocumentSlot(doc, uploaded)}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Updates Tab */}
            <TabsContent value="updates">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Progress Updates</CardTitle>
                  <CardDescription>
                    You'll receive updates every 35 days showing actions taken on your behalf.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {updates.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600">No updates yet</p>
                      <p className="text-sm text-slate-500">
                        Updates will appear here as we work on your Credit Restoration.
                      </p>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="timeline-line"></div>
                      <div className="space-y-6">
                        {updates.map((update) => (
                          <div key={update.id} className="relative pl-10">
                            <div className="timeline-dot absolute left-0"></div>
                            <div className="bg-slate-50 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-semibold text-slate-900">{update.title}</h4>
                                <span className="text-xs text-slate-500">
                                  {new Date(update.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <p className="text-slate-600 text-sm">{update.description}</p>
                              {update.actions_taken && update.actions_taken.length > 0 && (
                                <div className="mt-3">
                                  <p className="text-xs font-semibold text-slate-700 mb-2">Actions Taken:</p>
                                  <ul className="text-xs text-slate-600 space-y-1">
                                    {update.actions_taken.map((action, i) => (
                                      <li key={i} className="flex items-start gap-2">
                                        <CheckCircle className="h-3 w-3 text-teal-600 mt-0.5" />
                                        {action}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Credit Items Tab */}
            <TabsContent value="credit-items">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Credit Items Being Addressed</CardTitle>
                  <CardDescription>
                    Track the status of items we're disputing on your credit report.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {creditItems.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600">No credit items yet</p>
                      <p className="text-sm text-slate-500">
                        Items will appear here once we begin working on your case.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {creditItems.map((item) => (
                        <div 
                          key={item.id} 
                          className="flex items-start justify-between p-4 bg-slate-50 rounded-lg"
                        >
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-slate-900">{item.creditor_name}</span>
                              {getStatusBadge(item.status)}
                            </div>
                            <p className="text-sm text-slate-600 capitalize">{item.item_type.replace('_', ' ')}</p>
                            {item.amount && (
                              <p className="text-sm text-slate-500">Amount: ${item.amount.toFixed(2)}</p>
                            )}
                          </div>
                          {item.dispute_reason && (
                            <p className="text-xs text-slate-500 max-w-xs text-right">
                              {item.dispute_reason}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}
